import { CHARACTER_CONFIG } from '../../shared/constants/CharacterConfig';
import { EVENT_CONFIG } from '../../shared/constants/EventConfig';
import { GAMEPLAY_CONFIG } from '../../shared/constants/GameplayConfig';
import { NETWORK_CONFIG } from '../../shared/constants/NetworkConfig';
import { PHYSICS_CONFIG } from '../../shared/constants/PhysicsConfig';

export interface Position {
  x: number;
  y: number;
}

export interface MapConfig {
  id: string;
  name: string;
  width: number;
  height: number;
  backgroundColor: string;
  gravity: number;
  spawnPoints: Position[];
  defaultSpawn: Position;
  deathRespawn: Position;
}

export interface TeleporterConfig {
  id: string;
  x: number;
  y: number;
  targetMapId: string;
  targetSpawnId: string;
  isDefaultSpawn: boolean;
  requiresActivation: boolean;
  visualEffect: string;
  attributes?: Record<string, string>;
}

export interface MapConfigData {
  maps: Record<string, MapConfig>;
  teleporters: Record<string, TeleporterConfig[]>;
}

export interface GameConstants {
  physics: {
    gravity: number;
    terminalVelocity: number;
    airFriction: number;
    maxFallSpeed: number;
    coyoteTime: number;
    jumpBufferTime: number;
    cornerNudgeThreshold: number;
    npcDrag: number;
    npcGravity: number;
    platformCheckInterval: number;
  };
  gameplay: {
    respawnDelay: number;
    iFrameDuration: number;
    gcdDuration: number;
    attackRangeMelee: number;
    attackRangeRanged: number;
    knockbackForceX: number;
    knockbackForceY: number;
  };
  character: {
    defaultPosition: Position;
    defaultHealth: number;
    defaultMaxHealth: number;
    defaultGold: number;
    defaultStats: Record<string, number>;
  };
  enemies: {
    pig: {
      normal: {
        health: number;
        maxHealth: number;
        level: number;
        xpValue: number;
      };
      angry: {
        health: number;
        maxHealth: number;
        level: number;
        xpValue: number;
      };
    };
  };
  network: {
    connectionTimeout: number;
  };
  debug?: {
    showTeleporters?: boolean;
    currentMapId?: string;
  };
}

export interface EventConfig {
  map: Record<string, string>;
  combat: Record<string, string>;
  character: Record<string, string>;
  inventory: Record<string, string>;
  quest: Record<string, string>;
  ui: Record<string, string>;
}

import { LevelRegistry } from '../../game/systems/map/LevelRegistry';

class ConfigManager {
  private static instance: ConfigManager;

  public readonly gameConstants: GameConstants;
  // public readonly mapConfig: MapConfigData; // Removed
  public readonly events: EventConfig;

  private constructor() {
    this.gameConstants = {
      physics: {
        gravity: PHYSICS_CONFIG.GRAVITY.DEFAULT,
        terminalVelocity: PHYSICS_CONFIG.GRAVITY.TERMINAL_VELOCITY,
        airFriction: PHYSICS_CONFIG.GRAVITY.AIR_FRICTION,
        maxFallSpeed: PHYSICS_CONFIG.GRAVITY.MAX_FALL_SPEED,
        coyoteTime: PHYSICS_CONFIG.MOVEMENT.COYOTE_TIME,
        jumpBufferTime: PHYSICS_CONFIG.MOVEMENT.JUMP_BUFFER,
        cornerNudgeThreshold: PHYSICS_CONFIG.MOVEMENT.CORNER_NUDGE,
        npcDrag: PHYSICS_CONFIG.FRICTION.NPC_DRAG,
        npcGravity: PHYSICS_CONFIG.GRAVITY.NPC_GRAVITY,
        platformCheckInterval: PHYSICS_CONFIG.LOGIC.PLATFORM_CHECK_INTERVAL,
      },
      gameplay: {
        respawnDelay: GAMEPLAY_CONFIG.respawnDelay,
        iFrameDuration: GAMEPLAY_CONFIG.iFrameDuration,
        gcdDuration: GAMEPLAY_CONFIG.gcdDuration,
        attackRangeMelee: GAMEPLAY_CONFIG.attackRangeMelee,
        attackRangeRanged: GAMEPLAY_CONFIG.attackRangeRanged,
        knockbackForceX: GAMEPLAY_CONFIG.knockbackForceX,
        knockbackForceY: GAMEPLAY_CONFIG.knockbackForceY,
      },
      character: {
        defaultPosition: CHARACTER_CONFIG.defaultPosition,
        defaultHealth: CHARACTER_CONFIG.defaultHealth,
        defaultMaxHealth: CHARACTER_CONFIG.defaultMaxHealth,
        defaultGold: CHARACTER_CONFIG.defaultGold,
        defaultStats: CHARACTER_CONFIG.defaultStats as any,
      },
      enemies: {
        pig: {
          normal: {
            health: 50,
            maxHealth: 50,
            level: 2,
            xpValue: 25,
          },
          angry: {
            health: 75,
            maxHealth: 75,
            level: 3,
            xpValue: 35,
          },
        },
      },
      network: {
        connectionTimeout: NETWORK_CONFIG.connectionTimeout,
      },
    };
    this.events = EVENT_CONFIG;
  }

  public static getInstance(): ConfigManager {
    if (!ConfigManager.instance) {
      ConfigManager.instance = new ConfigManager();
    }
    return ConfigManager.instance;
  }

  // Refactored to use LevelRegistry
  public getMapConfig(mapId: string): MapConfig | undefined {
    const level = LevelRegistry.getInstance().getLevel(mapId);
    if (!level) return undefined;

    return {
      id: level.id,
      name: level.name,
      width: level.width,
      height: level.height,
      backgroundColor: level.backgroundColor,
      gravity: level.gravity,
      spawnPoints: level.spawnPoints || [],
      defaultSpawn: level.defaultSpawn || { x: 0, y: 0 },
      deathRespawn: level.defaultSpawn || { x: 0, y: 0 }
    };
  }

  // Removed getTeleporters() as it is no longer used (TeleporterManager sources directly from Level)

  public getEvent(eventPath: string): string | undefined {
    const parts = eventPath.split('.');
    let current: any = this.events;

    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        return undefined;
      }
    }

    return typeof current === 'string' ? current : undefined;
  }

  public getMapDeathRespawn(mapId: string): Position {
    const mapConfig = this.getMapConfig(mapId);
    return mapConfig?.deathRespawn || { x: 400, y: 300 };
  }

  public getMapDefaultSpawn(mapId: string): Position {
    const mapConfig = this.getMapConfig(mapId);
    return mapConfig?.defaultSpawn || { x: 400, y: 300 };
  }
}

export default ConfigManager;
